import { ConnectionQuality, Track, VideoQuality } from 'livekit-client';
import type { Room } from 'livekit-client';
import type { RemoteTrackPublication } from 'livekit-client';
import type { ScreenShareAdaptMode } from '@/lib/screen-share-profile';

/**
 * Regras de produto para tela compartilhada:
 * - Nunca forçar LOW/360p no subscribe da tela (economia, CPU, Poor).
 * - Poor no publicador só reduz FPS; Lost pode encolher resolução.
 * - Economia automática não liga enquanto a tela local está publicada.
 */

export function shouldForceLowIncomingQuality(source: Track.Source): boolean {
  return source !== Track.Source.ScreenShare;
}

export function shouldClearVirtualBackgroundOnEconomy(): boolean {
  return false;
}

export function shouldAutoActivateEconomy(input: {
  qualityIsPoor: boolean;
  screenSharing: boolean;
  alreadyEnabled: boolean;
}): boolean {
  if (!input.qualityIsPoor || input.alreadyEnabled || input.screenSharing) {
    return false;
  }
  return true;
}

/** Poor: mantém resolução do perfil. Lost: modo poor (540p@8). */
export function getScreenShareAdaptModeForConnection(
  quality: ConnectionQuality,
  economyEnabled: boolean,
): ScreenShareAdaptMode {
  if (quality === ConnectionQuality.Lost) {
    return 'poor';
  }
  if (economyEnabled) {
    return 'economy';
  }
  return 'default';
}

/**
 * Teto de FPS extra em Poor (sem cair resolução).
 * Lost usa o FPS do modo `poor` (8). Good/Excellent: null = perfil.
 */
export function getScreenShareFpsCapForConnection(
  quality: ConnectionQuality,
): number | null {
  if (quality === ConnectionQuality.Poor) {
    return 12;
  }
  return null;
}

export function pinIncomingScreenShareQuality(room: Room): void {
  room.remoteParticipants.forEach((participant) => {
    participant.videoTrackPublications.forEach((publication) => {
      if (!publication.isSubscribed || publication.kind !== Track.Kind.Video) {
        return;
      }
      if (publication.source !== Track.Source.ScreenShare) {
        return;
      }
      try {
        (publication as RemoteTrackPublication).setVideoQuality(VideoQuality.HIGH);
      } catch {
        // publicações sem simulcast podem não aceitar setVideoQuality
      }
    });
  });
}

export function applyIncomingVideoQualitySkippingScreenShare(
  room: Room,
  quality: VideoQuality,
): void {
  room.remoteParticipants.forEach((participant) => {
    participant.videoTrackPublications.forEach((publication) => {
      if (!publication.isSubscribed || publication.kind !== Track.Kind.Video) {
        return;
      }
      if (!shouldForceLowIncomingQuality(publication.source)) {
        try {
          (publication as RemoteTrackPublication).setVideoQuality(VideoQuality.HIGH);
        } catch {
          // ignore
        }
        return;
      }
      try {
        (publication as RemoteTrackPublication).setVideoQuality(quality);
      } catch {
        // ignore
      }
    });
  });
}
